import React from "react";

const DownloadButton = ({ onClick, disabled, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm font-semibold text-emerald-100 transition-colors hover:border-emerald-400 hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-40"
  >
    {label}
  </button>
);

const DataViewBulkDownloadControls = ({
  show = false,
  modeConfig,
  selectedRows,
  isSearchMode,
  batchOptions,
  selectedBatchValue,
  onSelectedBatchChange,
  isBulkDownloading,
  bulkDownloadDisabled,
  primaryDownloadLabel,
  onBulkDownload,
  downloadError,
  batchSize,
}) => {
  if (!show) {
    return null;
  }

  return (
    <div className="mt-5 flex flex-row items-center justify-end gap-4">
      {!modeConfig.showBulkBatchDownload ? (
        <div className="flex flex-col gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-medium text-emerald-100">
              {selectedRows.length} row{selectedRows.length === 1 ? "" : "s"} selected
            </p>
            <p className="text-xs leading-5 text-emerald-200/70">
              {modeConfig.selectedRowsHelpText}
            </p>
          </div>

          <DownloadButton
            onClick={onBulkDownload}
            disabled={bulkDownloadDisabled}
            label={primaryDownloadLabel}
          />
        </div>
      ) : isSearchMode ? (
        <div className="flex flex-col gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-medium text-emerald-100">
              {selectedRows.length ? "Selected rows ready" : "Search results mode"}
            </p>
            <p className="text-xs leading-5 text-emerald-200/70">
              {selectedRows.length
                ? "Download only the checked rows from the current page."
                : modeConfig.searchModeHelpText}
            </p>
          </div>

          <DownloadButton
            onClick={onBulkDownload}
            disabled={bulkDownloadDisabled}
            label={primaryDownloadLabel}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-3 rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:flex-row md:items-start">
          {selectedRows.length ? (
            <div className="flex-1 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
              <p className="text-sm font-medium text-emerald-100">
                {selectedRows.length} row{selectedRows.length === 1 ? "" : "s"} selected
              </p>
              <p className="mt-1 text-xs leading-5 text-emerald-200/70">
                {modeConfig.selectedRowsHelpText}
              </p>
            </div>
          ) : (
            <div className="relative group flex-1">
              <select
                value={selectedBatchValue}
                onChange={(event) => onSelectedBatchChange(event.target.value)}
                disabled={!batchOptions.length || isBulkDownloading}
                className="min-h-[3.25rem] w-full appearance-none rounded-xl border border-slate-700 bg-slate-950 px-4 pr-10 text-sm text-slate-100 outline-none transition-all hover:bg-slate-900 focus:border-sky-500/60 focus:ring-2 focus:ring-sky-500/20 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {batchOptions.length ? (
                  batchOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                ) : (
                  <option value="">No records available</option>
                )}
              </select>

              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
              <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-56 -translate-x-1/2 rounded-xl border border-sky-500/20 bg-slate-950/95 px-3 py-2 text-xs leading-5 text-sky-50 opacity-0 shadow-[0_12px_30px_rgba(2,6,23,0.45)] transition-all duration-200 group-hover:-translate-y-1 group-hover:opacity-100 group-focus-within:-translate-y-1 group-focus-within:opacity-100">
                Each bulk CSV batch includes up to {batchSize.toLocaleString()} records.
              </div>
            </div>
          )}
          <DownloadButton
            onClick={onBulkDownload}
            disabled={bulkDownloadDisabled}
            label={primaryDownloadLabel}
          />
        </div>
      )}

      {downloadError ? (
        <div className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
          {downloadError}
        </div>
      ) : null}
    </div>
  );
};

export default DataViewBulkDownloadControls;
